import get from 'lodash/get';
import isElement from 'lodash/isElement';

const WINDOW = typeof window !== 'undefined' ? window : null;
const MutationObs = (WINDOW === null || WINDOW === void 0 ? void 0 : WINDOW.MutationObserver) || (WINDOW === null || WINDOW === void 0 ? void 0 : WINDOW.WebKitMutationObserver) || (WINDOW === null || WINDOW === void 0 ? void 0 : WINDOW.MozMutationObserver) || null;
const requestAF = ((WINDOW === null || WINDOW === void 0 ? void 0 : WINDOW.requestAnimationFrame) || (WINDOW === null || WINDOW === void 0 ? void 0 : WINDOW.webkitRequestAnimationFrame) || (WINDOW === null || WINDOW === void 0 ? void 0 : WINDOW.mozRequestAnimationFrame) || (WINDOW === null || WINDOW === void 0 ? void 0 : WINDOW.msRequestAnimationFrame) || (WINDOW === null || WINDOW === void 0 ? void 0 : WINDOW.oRequestAnimationFrame) || (
// Fallback, but not a true polyfill
// Only needed for Opera Mini
cb => setTimeout(cb, 16))).bind(WINDOW);
// eslint-disable-next-line no-unsafe-optional-chaining
const cancelAF = ((WINDOW === null || WINDOW === void 0 ? void 0 : WINDOW.cancelAnimationFrame) || (WINDOW === null || WINDOW === void 0 ? void 0 : WINDOW.mozCancelAnimationFrame)).bind(WINDOW);
// Remove a node from DOM
const removeNode = el => {
  el && el.parentNode && el.parentNode.removeChild(el);
};
// Cause/wait-for an element to reflow its content (adjusting its height/width)
const reflow = el => {
  // Requesting an elements offsetHeight will trigger a reflow of the element content
  return get(el, ['offsetHeight'], null);
};
// Select all elements matching selector. Returns `[]` if none found
const selectAll = (selector, root) => [...(root && isElement(root) ? root : document).querySelectorAll(selector)];
// Select a single element, returns `null` if not found
const select = (selector, root) => (root && isElement(root) ? root : document).querySelector(selector) || null;
// Returns true if the parent element contains the child element
const contains = (parent, child) => parent && typeof parent.contains === 'function' ? parent.contains(child) : false;
// Get an element given an ID
const getById = id => document.getElementById(/^#/.test(id) ? id.slice(1) : id) || null;
// Add a class to an element
const addClass = (el, className) => {
  // We are checking for `el.classList` existence here since IE 11
  // returns `undefined` for some elements (e.g. SVG elements)
  if (className && isElement(el) && el.classList) {
    el.classList.add(className);
  }
};
// Remove a class from an element
const removeClass = (el, className) => {
  // We are checking for `el.classList` existence here since IE 11
  // returns `undefined` for some elements (e.g. SVG elements)
  if (className && isElement(el) && el.classList) el.classList.remove(className);
};
// Test if an element has a class
const hasClass = (el, className) => {
  // We are checking for `el.classList` existence here since IE 11
  // returns `undefined` for some elements (e.g. SVG elements)
  if (className && isElement(el) && el.classList) return el.classList.contains(className);
  return false;
};
// Set an attribute on an element
const setAttr = (el, attr, value) => {
  if (attr && isElement(el)) {
    el.setAttribute(attr, value);
  }
};
// Remove an attribute from an element
const removeAttr = (el, attr) => {
  if (attr && isElement(el)) {
    el.removeAttribute(attr);
  }
};
// Get an attribute value from an element
const getAttr = (el, attr) => attr && isElement(el) ? el.getAttribute(attr) : null;
// Determine if an attribute exists on an element
// Returns `true` or `false`, or `null` if element not found
const hasAttr = (el, attr) => attr && isElement(el) ? el.hasAttribute(attr) : null;
// Set an style property on an element
const setStyle = (el, prop, value) => {
  if (prop && isElement(el)) {
    el.style[prop] = value;
  }
};
// Remove an style property from an element
const removeStyle = (el, prop) => {
  if (prop && isElement(el)) {
    el.style[prop] = '';
  }
};
// Get an style property value from an element
// Returns `null` if not found
const getStyle = (el, prop) => prop && isElement(el) ? el.style[prop] || null : null;
// Return the Bounding Client Rect of an element
const getBCR = el => isElement(el) ? el.getBoundingClientRect() : null;
// Get computed style object for an element
const getCS = el => get(WINDOW, 'getComputedStyle', null) && isElement(el) ? getComputedStyle(el) : {};
// Return an element's offset with respect to document element
const offset = el => {
  const _offset = {
    top: 0,
    left: 0
  };
  if (!isElement(el) || el.getClientRects().length === 0) {
    return _offset;
  }
  const bcr = getBCR(el);
  if (bcr) {
    const win = el.ownerDocument.defaultView;
    _offset.top = bcr.top + get(win, 'pageYOffset', 0);
    _offset.left = bcr.left + get(win, 'pageXOffset', 0);
  }
  return _offset;
};
// Return an element's offset with respect to to its offsetParent
const position = el => {
  let _offset = {
    top: 0,
    left: 0
  };
  if (!isElement(el)) {
    return _offset;
  }
  let parentOffset = {
    top: 0,
    left: 0
  };
  const elStyles = getCS(el);
  if (elStyles.position === 'fixed') {
    _offset = getBCR(el) || _offset;
  } else {
    _offset = offset(el);
    const doc = el.ownerDocument;
    let offsetParent = el.offsetParent || doc.documentElement;
    while (offsetParent && (offsetParent === doc.body || offsetParent === doc.documentElement) && getCS(offsetParent).position === 'static') {
      offsetParent = offsetParent.parentNode;
    }
    if (offsetParent && offsetParent !== el && offsetParent.nodeType === Node.ELEMENT_NODE) {
      parentOffset = offset(offsetParent);
      const offsetParentStyles = getCS(offsetParent);
      parentOffset.top += parseFloat(offsetParentStyles.borderTopWidth);
      parentOffset.left += parseFloat(offsetParentStyles.borderLeftWidth);
    }
  }
  return {
    top: _offset.top - parentOffset.top - parseFloat(elStyles.marginTop),
    left: _offset.left - parentOffset.left - parseFloat(elStyles.marginLeft)
  };
};
const getMaxZIndex = element => {
  let maxZIndex = 0;
  while (element) {
    const cs = getCS(element);
    const zIndex = cs.zIndex;
    if (zIndex !== 'auto') {
      maxZIndex = +zIndex > maxZIndex ? +zIndex : maxZIndex;
    }
    element = element.parentElement;
  }
  return maxZIndex;
};

export { MutationObs as M, removeNode as a, reflow as b, cancelAF as c, select as d, contains as e, addClass as f, getById as g, removeClass as h, hasClass as i, setAttr as j, removeAttr as k, getAttr as l, hasAttr as m, setStyle as n, removeStyle as o, getStyle as p, getBCR as q, requestAF as r, selectAll as s, getCS as t, offset as u, position as v, getMaxZIndex as w };
